import React, { useEffect, useMemo, useRef, useState } from "react";
import { MainLayout } from "../../layout/mainLayout";
import { useAppDispatch, useAppSelector } from "../../../framework/customStore";
import { Box, Typography } from "@mui/material";
import { fetchAllVehicleCameras, fetchVehicleOptions } from "../../../store/vehicleSlice";
import { RichTreeView, useTreeViewApiRef } from "@mui/x-tree-view";
import { FlespiVideoTimelineData } from "../../../models/vehicle/flespiDataTypes";
import { loadVideoTimeLineForDate } from "../../../services/vehicleMediaService";
import { handleApiError } from "../../../models/store/storeEffects";
import { Timeline, TimelineSegment } from "./timeline/timeline";
import { getCachedRecordingsForDay, RecordingDetails, RecordingsCache, upsertDayRecordingsToCache } from "../recodingsCache/recordingsCache";
import { closeLiveStream, FlespiPlayerOperation, FlespiVideoStreamPlayer, FlespiVideoStreamPlayerHandle, openPlaybackStream } from "../../flespiVideoStreamPlayer/flespiVideoStreamPlayer";
import { LoadingIndicatorInline } from "../../framework/loadingIndicatorNew";
import dayjs, { Dayjs } from "dayjs";
import { SectionHeader } from "../../layout/sectionHeader";
import { useTranslation } from "react-i18next";
import { CameraTreeviewCustomizer, CustomTreeItemProps } from "./vehicleCameraTreeViewCustomizer";

interface CameraTreeItem {
    id: string;
    label: any;
    children?: CameraTreeItem[];
    data?: VehicleCamera;
}

interface VehicleCamera {
    deviceUnitName: string;
    channelName: string;
    channelIdx: number;
    deviceFlespiId: string;
    vehicleName: string;
    vehicleId: string;
}

export const VehicleCamerasView = () => {
    const { vehicleCameras, vehicles, vehicleCamerasLoading } = useAppSelector((state) => state.vehicle);
    const dispatch = useAppDispatch();
    const [loadingVideo, setLoadingVideo] = useState<boolean>(false);
    const [loadingTimeline, setLoadingTimeline] = useState<boolean>(false);
    const [selectedDate, setSelectedDate] = useState<Dayjs>(null);
    const [recordingsCache, setRecordingsCache] = useState<RecordingsCache>({});
    const [activeTimelineSegments, setActiveTimeLineSegments] = useState<TimelineSegment[]>([]);
    const playerRef = useRef<FlespiVideoStreamPlayerHandle | null>(null);
    const apiRef = useTreeViewApiRef();
    const [selectedCamData, setSelectedCamData] = useState<VehicleCamera>(null);
    const [playingItemId, setPlayingItemId] = useState<string>(null);
    const [selectedItemId, setSelectedItemId] = useState<string>(null);
    const [isPlayback, setIsPlayback] = useState<boolean>(false);
    const { t } = useTranslation();

    const setItemPlaying = (itemId: string, playing: boolean) => {
        setPlayingItemId(playing ? itemId : null);
    };

    /*
     Build grid data from vehicles and their cameras
    */
    const treeItems = useMemo<CameraTreeItem[]>(() => {
        return (vehicleCameras ?? []).map(vehicle => ({
            id: `vehicle-${vehicle.vehicleId}`,
            label:
                vehicles.find(v => v.id === vehicle.vehicleId)?.registerNumber ?? "",
            children: (vehicle.cameraDevices ?? []).map(device => ({
                id: `device-${device.id}`,
                label:
                    !device.error ? device.name ?? t("vehicleCameraView.unkownDevice") : t("vehicleCameraView.deviceFetchFailed"),
                children: (device.configuration?.cameras ?? []).map(cam => ({
                    id: `camera-${device.id}-${cam.channel}`,
                    label: `${cam.name ?? t("vehicleCameraView.defaultCameraLabel")}`,
                    data: {
                        vehicleId: vehicle.vehicleId,
                        vehicleName: vehicles.find(v => v.id === vehicle.vehicleId)?.registerNumber ?? "",
                        deviceFlespiId: device.id.toString(),
                        deviceUnitName: device.name,
                        channelIdx: cam.channel,
                        channelName: cam.name,
                    } as VehicleCamera
                }))
            }))
        }));
    }, [vehicleCameras, vehicles]);

    /*
    Load related data to state if not loaded yet
    */
    useEffect(() => {
        if (vehicles === null || vehicles.length === 0) {
            dispatch(fetchVehicleOptions() as any);
        }
        if (vehicleCameras === null || vehicleCameras.length === 0) {
            dispatch(fetchAllVehicleCameras() as any);
        }
    }, []);

    /*
    Helpers
    */
    function getDateFromSeconds(
        secondsSinceDayStart: number,
        referenceDate: Dayjs = dayjs()
    ): Dayjs {
        return referenceDate
            .startOf("day")
            .add(secondsSinceDayStart, "second");
    }
    function getSecondsSinceMidnight(
        timestampSeconds: number,
        referenceDate: Dayjs
    ): number {
        const dayStartSeconds = referenceDate
            .startOf("day")
            .unix();

        return timestampSeconds - dayStartSeconds;
    }

    /*
    Event handlers
    */
    const onSelectTimeLineSegment = (selectedSegment: TimelineSegment) => {
        const fromDateTime = getDateFromSeconds(selectedSegment.startSeconds, selectedDate);
        setItemPlaying(selectedItemId, true);
        setIsPlayback(true);
        openPlaybackStream(
            playerRef,
            selectedCamData.deviceFlespiId,
            selectedCamData.channelIdx,
            fromDateTime.toDate(),
            60 * 5);
    };

    const closeCurrentStream = () => {
        setLoadingVideo(false);
        if (playingItemId) {
            closeLiveStream(
                playerRef,
                selectedCamData.deviceFlespiId,
                selectedCamData.channelIdx
            );
        }
        setIsPlayback(false);
    };

    // This callback event handler is called from treeview item when user date under camera row
    const onSelectCameraAndDate = (
        camData: VehicleCamera,
        itemId: string,
        selectedDate?: Dayjs
    ) => {
        // Closes stream from currently playing device/channel
        closeCurrentStream();

        const camChanged = (camData?.deviceFlespiId !== selectedCamData?.deviceFlespiId || camData?.channelIdx !== selectedCamData?.channelIdx);

        setSelectedDate(selectedDate);
        setSelectedCamData(camData);
        setActiveTimeLineSegments([]);
        setSelectedItemId(itemId);

        if (camChanged) {
            // Closes stream from new device/channel before we try to open new stream
            closeCurrentStream();
        }

        if (selectedDate && camData) {
            setLoadingTimeline(true);
            const cachedRecords = getCachedRecordingsForDay(
                recordingsCache,
                camData.vehicleId,
                camData.deviceFlespiId,
                camData.channelIdx,
                selectedDate.toISOString()
            );

            if (cachedRecords) {
                // Load cached records
                setActiveTimeLineSegments(cachedRecords);
                setLoadingTimeline(false);
            } else {
                // Load timeline entries and cache
                setLoadingTimeline(true);

                loadVideoTimeLineForDate(camData.deviceFlespiId, camData.channelIdx, selectedDate.toDate())
                    .then((res) => {
                        if (res.data?.result[0]?.response?.data !== null) {
                            const data = res.data?.result[0]?.response?.data as FlespiVideoTimelineData[];

                            const recordingsArray = data.map((d) => {
                                return {
                                    startSeconds: getSecondsSinceMidnight(d.begin, selectedDate),
                                    endSeconds: getSecondsSinceMidnight(d.end, selectedDate),
                                } as RecordingDetails;
                            });

                            setActiveTimeLineSegments(recordingsArray);

                            setRecordingsCache(prev =>
                                upsertDayRecordingsToCache(
                                    prev,
                                    camData.vehicleId,
                                    camData.deviceFlespiId,
                                    camData.channelIdx,
                                    selectedDate.toISOString(),
                                    recordingsArray
                                )
                            );
                        }
                    }).catch((er) => {
                        handleApiError(t("vehicleCameraView.timelineLoadFailed"), dispatch);
                        setActiveTimeLineSegments([]);
                    }).finally(() => {
                        setLoadingTimeline(false);
                    });
            }
        }
    };

    const onStreamOperationError = (operation: FlespiPlayerOperation, error: any) => {
        setPlayingItemId(null);
        setLoadingVideo(false);
        switch (operation) {
            case FlespiPlayerOperation.startLiveStream: {
                handleApiError(t("vehicleCameraView.playbackFailed"), dispatch);
            }
            case FlespiPlayerOperation.closeStream: {
                // Don't show error to user when closing stream
            }
            case FlespiPlayerOperation.playbackStream: {
                handleApiError(t("vehicleCameraView.playbackFailed"), dispatch);
            }
        }

    };

    const onStreamOperationException = (operation: FlespiPlayerOperation, message: any, ex: any) => {
        setPlayingItemId(null);
        setLoadingVideo(false);
        switch (operation) {
            case FlespiPlayerOperation.startLiveStream: {
                handleApiError(t("vehicleCameraView.playbackFailed"), dispatch);
            }
            case FlespiPlayerOperation.closeStream: {
                // Don't show error to user when closing stream
            }
            case FlespiPlayerOperation.playbackStream: {
                handleApiError(t("vehicleCameraView.playbackFailed"), dispatch);
            }
        }
    };

    const renderStatusText = (text: string) => {
        return (
            <Typography
                marginBottom={0}
                variant="subtitle1"
                display="inline-block"
                color="primary.main"
                alignSelf="flex-start"
                fontStyle="italic"
                sx={{ verticalAlign: "middle" }}
            >
                {text}
            </Typography>
        )
    };


    return (
        <MainLayout
            noPadding
            sideComponentSide="left"
            sideComponent={
                <>
                    <Box width={375} height="100%" display="flex" flexDirection="column">
                        <SectionHeader>
                            <Box>
                                <Typography
                                    marginBottom={0}
                                    variant="h3"
                                    display="inline-block"
                                    color="primary.contrastText"
                                    alignSelf="center"
                                    sx={{ verticalAlign: "middle" }}
                                >
                                    {t("vehicleCameraView.camerasHeader")}
                                </Typography>
                            </Box>
                        </SectionHeader>

                        {vehicleCamerasLoading &&
                            <LoadingIndicatorInline loading={vehicleCamerasLoading} tooltip={t("vehicleCameraView.loadingCameras")} />
                        }
                        {treeItems && treeItems.length > 0 ? (
                            <Box
                                flex={1}
                                overflow="auto"
                                sx={{
                                    px: 1,
                                    py: 0.5,
                                    backgroundColor: "background.paper",
                                }}
                            >
                                <RichTreeView
                                    apiRef={apiRef}
                                    items={treeItems}
                                    slots={{
                                        item: (props: CustomTreeItemProps) => {
                                            return (
                                                <CameraTreeviewCustomizer
                                                    {...props}
                                                    playerRef={playerRef}
                                                    onSelectCameraAndDate={onSelectCameraAndDate}
                                                    setItemPlaying={setItemPlaying}
                                                    playing={playingItemId === props.itemId}
                                                    selectedDate={props.itemId === selectedItemId ? selectedDate : null}
                                                    closeCurrentStream={closeCurrentStream}
                                                    isPlayback={isPlayback}
                                                    selectedCamData={setSelectedCamData}
                                                    isSelected={props.itemId === selectedItemId}
                                                />);
                                        }
                                    }}
                                />
                            </Box>
                        ) :
                            (
                                !vehicleCamerasLoading && renderStatusText(t("vehicleCameraView.noCameraDevicesAvaible"))
                            )
                        }
                    </Box>
                </>
            }
            sideOverFlowHidden
        >
            <Box width="100%" height="100%" display="flex" flexDirection="column">
                {selectedCamData && (
                    <>
                        <SectionHeader>
                            <Box display="block">
                                <Typography
                                    marginBottom={0}
                                    variant="h3"
                                    color="primary.contrastText"
                                    alignSelf="center"
                                    sx={{ verticalAlign: "middle" }}
                                >
                                    {t("vehicleCameraView.vehicleHeaderLabelFormat", { name: selectedCamData.vehicleName })}
                                </Typography>

                                <Typography
                                    marginBottom={0}
                                    variant="h4"
                                    color="primary.contrastText"
                                    alignSelf="center"
                                    sx={{ verticalAlign: "middle" }}
                                >
                                    {selectedCamData.channelName ?? t("vehicleCameraView.defaultCameraLabel")}
                                </Typography>
                            </Box>
                        </SectionHeader>
                    </>
                )}
                {selectedDate && (
                    <SectionHeader>
                        <Box>
                            <Typography
                                marginBottom={0}
                                variant="h4"
                                display="inline-block"
                                color="primary.contrastText"
                                alignSelf="center"
                                sx={{ verticalAlign: "middle" }}
                            >
                                {t("vehicleCameraView.recordingsHeaderLabelFormat", { date: selectedDate.format("DD.MM.YYYY") })}
                            </Typography>
                        </Box>
                    </SectionHeader>
                )}
                {selectedDate && (
                    <Box>
                        <Timeline
                            timelineStart={0}
                            timelineEnd={86400}
                            segments={activeTimelineSegments}
                            onSeek={onSelectTimeLineSegment}
                        />
                    </Box>
                )
                }
                {loadingTimeline && (
                    <>
                        <Box>
                            {renderStatusText(t("vehicleCameraView.loadingTimelineTooltip"))}
                        </Box>
                        <LoadingIndicatorInline loading={loadingTimeline} tooltip={t("vehicleCameraView.loadingTimelineTooltip")} />
                    </>
                )
                }

                {loadingVideo && (
                    <>
                        <Box>
                            {renderStatusText(t("vehicleCameraView.loadingVideoTooltip"))}
                        </Box>
                        <LoadingIndicatorInline loading={loadingVideo} tooltip={t("vehicleCameraView.loadingVideoTooltip")} />
                    </>
                )
                }
                <FlespiVideoStreamPlayer
                    ref={playerRef}
                    onLoadingChange={(loading) => setLoadingVideo(loading)}
                    onStreamOperationError={onStreamOperationError}
                    onStreamOperationException={onStreamOperationException}
                />
            </Box>
        </MainLayout>
    );
};
